import { ACTIVE_CASE_STATUSES } from '#entities/Case';
import { ENOPCaseStatus } from '#entities/enums';
import { NEW_CASE_STATUSES } from '#entities/NewCase';

import type { Point } from '#shared';
import type { MapSnapshotCaseBuffer, MapSnapshotCaseEvent } from '../api';

export type HistoricalCaseKind = 'new' | 'active';

export interface HistoricalCase {
  readonly id: string;
  readonly kind: HistoricalCaseKind;
  readonly status: ENOPCaseStatus;
  readonly label: string;
  readonly donorId: string | null;
  readonly organ: string | null;
  readonly serviceRegionId: string | null;
  readonly donorLonLat: Point | null;
  readonly recipientLonLat: Point | null;
}

interface ReplayState {
  status: string | null;
  baselineLabel: string | null;
  donorId: string | null;
  organ: string | null;
  serviceRegionId: string | null;
  donorLonLat: Point | null;
  recipientLonLat: Point | null;
}

type Payload = Record<string, unknown>;

const OPENER_TYPES: ReadonlySet<string> = new Set([
  'CaseDraftCreated',
  'CaseTxCenterDraftCreated',
  'CaseClinicalScreeningCreated',
  'CaseOpoDraftCreated',
  'CaseBaselineCreated',
]);

const isPayload = (value: unknown): value is Payload =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asString = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

const isCoordinate = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value !== 0;

// Upstream publishes 0 for an unset hospital location.
const toLonLat = (longitude: unknown, latitude: unknown): Point | null =>
  isCoordinate(longitude) && isCoordinate(latitude)
    ? [longitude, latitude]
    : null;

// The details strings are serialized upstream with key casing we cannot pin, so a camelCase miss falls back to any casing.
const readKey = (object: Payload, key: string): unknown => {
  if (key in object) {
    return object[key];
  }
  const lowered = key.toLowerCase();
  const match = Object.keys(object).find(k => k.toLowerCase() === lowered);
  return match === undefined ? undefined : object[match];
};

const parseDetailsLonLat = (details: unknown): Point | null => {
  if (typeof details !== 'string') {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(details);
  } catch {
    return null;
  }
  if (!isPayload(parsed)) {
    return null;
  }
  const location = readKey(parsed, 'location');
  if (!isPayload(location)) {
    return null;
  }
  return toLonLat(
    readKey(location, 'longitude'),
    readKey(location, 'latitude'),
  );
};

const baselineLonLat = (hospital: unknown): Point | null => {
  if (!isPayload(hospital) || !Array.isArray(hospital.lonLat)) {
    return null;
  }
  const [longitude, latitude] = hospital.lonLat as unknown[];
  return toLonLat(longitude, latitude);
};

const projectCreate = (payload: Payload): ReplayState => ({
  status: asString(payload.status),
  baselineLabel: null,
  donorId: asString(payload.donorId),
  organ: asString(payload.organType),
  serviceRegionId: asString(payload.serviceRegionId),
  donorLonLat: parseDetailsLonLat(payload.donorHospitalDetails),
  recipientLonLat: parseDetailsLonLat(payload.txCenterDetails),
});

const projectBaseline = (payload: Payload): ReplayState => ({
  status: asString(payload.status),
  baselineLabel: asString(payload.label),
  donorId: asString(payload.unosDonorId),
  organ: isPayload(payload.organ) ? asString(payload.organ.name) : null,
  serviceRegionId: asString(payload.serviceRegionId),
  donorLonLat: baselineLonLat(payload.donorHospital),
  recipientLonLat: baselineLonLat(payload.recipientHospital),
});

const isClearedId = (field: unknown): boolean =>
  isPayload(field) && field.valueSpecified === true && field.value === null;

const patchPlace = (
  current: Point | null,
  details: unknown,
  id: unknown,
): Point | null => {
  if (typeof details === 'string' && details !== '') {
    return parseDetailsLonLat(details);
  }
  return isClearedId(id) ? null : current;
};

// A null scalar in a details patch means "unchanged", not "cleared".
const applyDetails = (state: ReplayState, patch: Payload): ReplayState => ({
  ...state,
  donorId: asString(patch.donorId) ?? state.donorId,
  organ: asString(patch.organType) ?? state.organ,
  serviceRegionId: asString(patch.serviceRegionId) ?? state.serviceRegionId,
  donorLonLat: patchPlace(
    state.donorLonLat,
    patch.donorHospitalDetails,
    patch.donorHospitalId,
  ),
  recipientLonLat: patchPlace(
    state.recipientLonLat,
    patch.txCenterDetails,
    patch.txCenterId,
  ),
});

const applyEvent = (
  state: ReplayState,
  event: MapSnapshotCaseEvent,
): ReplayState => {
  if (!isPayload(event.payload)) {
    return state;
  }
  if (event.type === 'CaseStatusUpdated') {
    return {
      ...state,
      status: asString(event.payload.newStatus) ?? state.status,
    };
  }
  if (event.type === 'CaseDetailsUpdated') {
    return applyDetails(state, event.payload);
  }
  return state;
};

const isStatusIn = (
  statuses: readonly ENOPCaseStatus[],
  status: string,
): status is ENOPCaseStatus => (statuses as readonly string[]).includes(status);

const findOpenerIndex = (buffer: MapSnapshotCaseBuffer): number =>
  buffer.events.findIndex(event => OPENER_TYPES.has(event.type));

const opensBy = (buffer: MapSnapshotCaseBuffer, tMs: number): boolean => {
  const opener = buffer.events[findOpenerIndex(buffer)];
  return opener !== undefined && opener.observedAtMs <= tMs;
};

export const replayCase = (
  buffer: MapSnapshotCaseBuffer,
  tMs: number,
): HistoricalCase | null => {
  const openerIndex = findOpenerIndex(buffer);
  const opener = buffer.events[openerIndex];
  if (
    opener === undefined ||
    opener.observedAtMs > tMs ||
    !isPayload(opener.payload)
  ) {
    return null;
  }

  const opened =
    opener.type === 'CaseBaselineCreated'
      ? projectBaseline(opener.payload)
      : projectCreate(opener.payload);
  const state = buffer.events
    .slice(openerIndex + 1)
    .filter(event => event.observedAtMs <= tMs)
    .reduce(applyEvent, opened);

  const { status } = state;
  if (status === null) {
    return null;
  }
  let kind: HistoricalCaseKind;
  if (isStatusIn(NEW_CASE_STATUSES, status)) {
    kind = 'new';
  } else if (isStatusIn(ACTIVE_CASE_STATUSES, status)) {
    kind = 'active';
  } else {
    return null;
  }

  return {
    id: buffer.caseId,
    kind,
    status,
    label:
      buffer.caseLabel ?? state.baselineLabel ?? state.donorId ?? buffer.caseId,
    donorId: state.donorId,
    organ: state.organ,
    serviceRegionId: state.serviceRegionId,
    donorLonLat: state.donorLonLat,
    recipientLonLat: state.recipientLonLat,
  };
};

const KNOWN_STATUSES: ReadonlySet<string> = new Set(
  Object.values(ENOPCaseStatus),
);

const statusesOf = (
  buffer: MapSnapshotCaseBuffer,
  opener: MapSnapshotCaseEvent,
): unknown[] => [
  isPayload(opener.payload) ? opener.payload.status : undefined,
  ...buffer.events
    .filter(event => event.type === 'CaseStatusUpdated')
    .map(event =>
      isPayload(event.payload) ? event.payload.newStatus : undefined,
    ),
];

/**
 * Why `replayCase` would drop this buffer for bad data rather than by design,
 * or `null`. A known status outside the drawn sets is by design.
 */
export const unreplayableReason = (
  buffer: MapSnapshotCaseBuffer,
): string | null => {
  const opener = buffer.events[findOpenerIndex(buffer)];
  if (opener === undefined) {
    return null;
  }
  if (!isPayload(opener.payload)) {
    return `${opener.type} payload is not an object`;
  }
  const unknown = statusesOf(buffer, opener).find(
    status => typeof status === 'string' && !KNOWN_STATUSES.has(status),
  );
  return unknown === undefined ? null : `unknown status ${String(unknown)}`;
};

/** A baseline refresh between T and the end edge moves the opener past T. Then only the start buffer still draws the case. */
export const pickCaseBuffer = (
  end: MapSnapshotCaseBuffer | undefined,
  start: MapSnapshotCaseBuffer | undefined,
  tMs: number,
): MapSnapshotCaseBuffer | undefined =>
  end !== undefined && opensBy(end, tMs) ? end : start;

export const replayCasesAt = (
  endBuffers: readonly MapSnapshotCaseBuffer[],
  startBuffers: readonly MapSnapshotCaseBuffer[],
  tMs: number,
): HistoricalCase[] => {
  const startById = new Map(
    startBuffers.map(buffer => [buffer.caseId, buffer]),
  );
  const endIds = new Set(endBuffers.map(buffer => buffer.caseId));
  const picked = [
    ...endBuffers.map(end =>
      pickCaseBuffer(end, startById.get(end.caseId), tMs),
    ),
    ...startBuffers.filter(start => !endIds.has(start.caseId)),
  ];
  return picked.flatMap(buffer => {
    const replayed = buffer === undefined ? null : replayCase(buffer, tMs);
    return replayed === null ? [] : [replayed];
  });
};
